import { createContext, Dispatch, SetStateAction } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';

export interface UnloadPromptContext {
  /**
   * The canonical "should block navigation" boolean. Auto-synced
   * from the OR of every registered subscriber, so multiple forms
   * on the same page can each report their dirty state without
   * racing. Imperative writes via `setShouldBlockNavCtx` apply
   * directly but are clobbered the next time any subscriber's
   * value changes — they're a short-lived override, not a
   * persistent contribution.
   */
  shouldBlockNavCtx: boolean;
  /**
   * Imperative setter that writes directly to `shouldBlockNavCtx`.
   * Use for a one-shot bypass before programmatic navigation
   * (e.g. `setShouldBlockNavCtx(false)` inside `flushSync` right
   * before `navigate(...)` after a successful save). Subsequent
   * subscriber changes resync the boolean from the subscriber
   * registry, so don't rely on this for long-lived overrides —
   * push your dirty state through `useOnBeforeUnloadPrompt`
   * instead.
   */
  setShouldBlockNavCtx: Dispatch<SetStateAction<boolean>>;
  /**
   * Register or update a subscriber's current "should block" value.
   * Callers should pass a stable `id` (e.g. from React's `useId`) so
   * repeat calls upsert rather than create new entries.
   */
  registerBlockSubscriber: (id: string, shouldBlock: boolean) => void;
  /** Remove a subscriber registered with `registerBlockSubscriber`. */
  unregisterBlockSubscriber: (id: string) => void;
}

export const UnloadPromptContext = createContext<UnloadPromptContext>({
  shouldBlockNavCtx: false,
  setShouldBlockNavCtx: () => null,
  registerBlockSubscriber: () => null,
  unregisterBlockSubscriber: () => null,
});

export const useUnloadPromptContext = () => {
  return useGuardedContext(UnloadPromptContext, 'UnloadPromptProvider');
};
